// Nomad-mon Go! — SC33 COMBAT DEPTH. Pure, deterministic, no DOM/audio/THREE.
//
// WHY THIS EXISTS
// SC32 made the opponent fight back. It did not make the COLLECTION matter. With
// 1,558 cards and a 5-card deck, the optimal deck was provably "your five highest
// cardPower", and every card of a given power played identically because all 1,558
// share the same eight keywords. The sim agreed: swapping any legendary for any
// other legendary of equal power moved the win-rate by less than a point.
//
// Four systems here give the collection a reason to be large, and give a battle a
// reason to be different from the last one:
//
//   SYNERGIES  — deck COMPOSITION pays. Type arcs, person cycles, and an underdog
//                bonus so a small collection is not simply worse.
//   ARENAS     — the six type arenas get a mechanical identity, applied to BOTH
//                sides equally, so the arena is a puzzle rather than wallpaper.
//   MOMENTUM   — the card that walks in over a fallen ally enters ENRAGED. Drama,
//                and a measured anti-snowball.
//   PASSIVES   — LEGENDARY / MYTHIC / ALT / ETERNAL cards each carry ONE signature
//                passive from a curated set of 16, so chase cards play differently
//                instead of merely having bigger numbers.
//
// EVERY number in this file was tuned against scripts/engine-sim.js and
// scripts/sc33-sim.js. Nothing here is asserted; the tables are in the run log.
// Nothing here consumes rng — all of it is a pure function of the deck ids, the
// card data and the arena, so multiplayer lockstep is unaffected.

export const TYPE_ORDER = ['IRON', 'VOYAGE', 'HUSTLE', 'MYSTIC', 'CHAOS', 'HEART'];

// ============================================================ 1. SYNERGIES
// A synergy is a named, explained bonus computed ONCE at battle start from the
// five character ids. The bonus vocabulary is deliberately tiny so the cap is
// enforceable and so the sim can attribute a win-rate delta to a single term:
//
//   dmgPct       +N% to every attack this side makes
//   energyPerRound  +N energy at end of round
//   chargeFaster    v10: N x SURGE_START_PER_CHARGE starting surge (was: N fewer charge pips)
//   surgeStart      v10 CREW MASTERED: N x SURGE_START_PER_CHARGE starting surge
//   neutralizeFirstWeakness  v10 SPECTRUM: the first super-effective hit this
//                   side TAKES lands neutral (once per battle)
//
// THE CAP (measured, then NERFED). The worst legal stack is a GRAND ARC plus a
// person cycle plus UNDERDOG on one deck — raw +21% damage and +1 energy. A
// first pass capped damage at 12% and the sim measured that stack at +11.3pp
// for the good referee, against an +8pp budget. The cap is 8%.
export const SYNERGY_CAP = { dmgPct: 8, energyPerRound: 1, chargeFaster: 1, surgeStart: 3 };
// v10 COLLECTION/DECK V2: a nomad WHEEL's grade -> CREW MASTERED starting surge.
export const MASTERY_SURGE = { COMPLETE: 1, GOLD: 2, PRISM: 3 };

const RARITY_RANK = { common: 0, rare: 1, epic: 2, legendary: 3, mythic: 4, eternal: 5 };
export function rarityRank(c) { return RARITY_RANK[(c && c.rarity) || 'common'] ?? 0; }

// The LABBORN family. Four carry the name, one carries the crown; `cycle:
// 'LABBORN'` is on three of them in cards.json, so membership is the union of
// the explicit tag and the known five ids — a card cannot join by being renamed.
export const LABBORN_IDS = [
  'labborn-james', 'labborn-chia', 'labborn-damon',
  'king-hooman-james', 'labborn-james-king',
];
function isLabborn(c) {
  return !!c && (c.cycle === 'LABBORN' || LABBORN_IDS.includes(c.id));
}

// Longest run of consecutive types on the six-type wheel, counting each type
// once. The wheel is a CIRCLE, so HEART->IRON->VOYAGE is an arc of three.
export function longestTypeArc(types) {
  const have = new Set(types.filter(t => TYPE_ORDER.includes(t)));
  if (have.size <= 1) return { len: have.size, arc: [...have] };
  if (have.size === 6) return { len: 6, arc: [...TYPE_ORDER] };
  let best = { len: 0, arc: [] };
  for (let s = 0; s < 6; s++) {
    const arc = [];
    for (let k = 0; k < 6; k++) {
      const t = TYPE_ORDER[(s + k) % 6];
      if (!have.has(t)) break;
      arc.push(t);
    }
    if (arc.length > best.len) best = { len: arc.length, arc };
  }
  return best;
}

// detectSynergies(charIds, byId) -> { list, bonus, capped }
// list: [{ key, label, blurb, detail, bonus }] in a stable display order.
// SC38e: the arena banners lead with the EFFECT and drop the flavour clause.
// Same instinct as the TYPE ARC badge — "The air is thick with it." is colour,
// "Specials charge a round faster" is the thing that changes how you play the
// round, and on a Fold the whole sentence was painted across both fighters.
// bonus: the CAPPED sum actually applied to the battle.
// `mastery` (v10): { person: 'COMPLETE'|'GOLD'|'PRISM' } from the deck snapshot
// agreed at match start (deck.mastery). It only counts for the person whose
// CREW (person cycle) bond is active in this deck.
export function detectSynergies(charIds, byId, mastery = null) {
  const cards = (charIds || []).map(id => byId[id]).filter(Boolean);
  const list = [];
  if (!cards.length) return { list, bonus: { dmgPct: 0, energyPerRound: 0, chargeFaster: 0, surgeStart: 0, neutralizeFirstWeakness: false }, capped: false };

  // -- TYPE ARC ------------------------------------------------------------
  const arc = longestTypeArc(cards.map(c => c.type));
  if (arc.len >= 3) {
    const big = arc.len >= 4;
    list.push({
      key: big ? 'arc4' : 'arc3',
      label: big ? 'GRAND ARC' : 'TYPE ARC',
      blurb: big
        ? 'Four or more types in an unbroken run on the wheel. +1 energy a round and +5% damage.'
        : 'Three types in an unbroken run on the wheel. +1 energy a round and +3% damage.',
      detail: arc.arc.join(' → '),
      // +1 energy alone measured +0.8pp — a real bonus, but too quiet to be
      // worth building toward. The small damage rider gives the arc some teeth.
      bonus: big ? { energyPerRound: 1, dmgPct: 5 } : { energyPerRound: 1, dmgPct: 3 },
    });
  }

  // -- PERSON CYCLE --------------------------------------------------------
  const byPerson = {};
  for (const c of cards) if (c.person) (byPerson[c.person] ||= []).push(c);
  let bestPerson = null;
  for (const [p, cs] of Object.entries(byPerson)) {
    if (cs.length >= 3 && (!bestPerson || cs.length > bestPerson.cs.length)) bestPerson = { p, cs };
  }
  if (bestPerson) {
    list.push({
      key: 'person',
      label: `${bestPerson.p.toUpperCase()} CYCLE`,
      blurb: `Three or more printings of the same nomad. They fight like they know each other: +5% damage.`,
      detail: bestPerson.cs.map(c => c.name).join(' · '),
      bonus: { dmgPct: 5 },     // 6% measured +7.0pp — trimmed for headroom
    });
    // v10 CREW MASTERED: that nomad's WHEEL is complete in the collection.
    const grade = mastery && Object.prototype.hasOwnProperty.call(mastery, bestPerson.p)
      ? mastery[bestPerson.p] : null;
    const pips = MASTERY_SURGE[grade] || 0;
    if (pips) {
      list.push({
        key: 'crewMastered',
        label: `${bestPerson.p.toUpperCase()} MASTERED`,
        blurb: `Their wheel is ${grade} in your binder. The crew starts with surge banked.`,
        detail: grade,
        bonus: { surgeStart: pips },
      });
    }
  }

  // -- SPECTRUM (v10) --------------------------------------------------------
  // Five different types: the first super-effective hit this side takes lands
  // neutral. One shot per battle.
  if (cards.length >= 5 && new Set(cards.map(c => c.type)).size >= 5) {
    list.push({
      key: 'spectrum',
      label: 'SPECTRUM',
      blurb: 'Five different types. The first super-effective hit you take lands neutral.',
      detail: [...new Set(cards.map(c => c.type))].join(' · '),
      bonus: { neutralizeFirstWeakness: true },
    });
  }

  // -- THE LABBORN CYCLE ---------------------------------------------------
  // The distinct, stronger one. Four of the five is the "complete" cycle; the
  // fifth is a flourish, not a further bonus (it would push past the cap alone).
  const lab = cards.filter(isLabborn);
  if (lab.length >= 4) {
    list.push({
      key: 'labborn',
      label: 'THE LABBORN CYCLE',
      blurb: 'The lab, whole. Starts with surge banked and every attack lands +6%.',
      detail: lab.map(c => c.name).join(' · '),
      bonus: { dmgPct: 6, chargeFaster: 1 },   // 8% measured +7.8pp
    });
  } else if (lab.length === 3) {
    list.push({
      key: 'labborn3',
      label: 'LABBORN PARTIAL',
      blurb: 'Three of the five. The lab remembers: starts with surge banked.',
      detail: lab.map(c => c.name).join(' · '),
      bonus: { chargeFaster: 1 },
    });
  }

  // -- UNDERDOG ------------------------------------------------------------
  // Nothing above epic. This is the bonus that keeps a 30-card collection in the
  // fight, so it is the largest single damage term in the file.
  const top = Math.max(...cards.map(rarityRank));
  if (top <= RARITY_RANK.epic && cards.length >= 3) {
    list.push({
      key: 'underdog',
      label: 'UNDERDOG',
      blurb: 'Not one card above EPIC. Nobody expects you: +8% damage.',
      detail: `highest rarity: ${(cards.find(c => rarityRank(c) === top) || {}).rarity || 'common'}`,
      bonus: { dmgPct: 8 },     // 10% measured +7.7pp — the comeback term, kept large
    });
  }

  // -- sum, then CAP -------------------------------------------------------
  const raw = { dmgPct: 0, energyPerRound: 0, chargeFaster: 0, surgeStart: 0 };
  for (const s of list) for (const k of Object.keys(raw)) raw[k] += s.bonus[k] || 0;
  const bonus = {
    dmgPct: Math.min(raw.dmgPct, SYNERGY_CAP.dmgPct),
    energyPerRound: Math.min(raw.energyPerRound, SYNERGY_CAP.energyPerRound),
    chargeFaster: Math.min(raw.chargeFaster, SYNERGY_CAP.chargeFaster),
    surgeStart: Math.min(raw.surgeStart, SYNERGY_CAP.surgeStart),
    neutralizeFirstWeakness: list.some(s => s.bonus.neutralizeFirstWeakness),
  };
  const capped = bonus.dmgPct !== raw.dmgPct || bonus.energyPerRound !== raw.energyPerRound
    || bonus.chargeFaster !== raw.chargeFaster || bonus.surgeStart !== raw.surgeStart;
  return { list, bonus, raw, capped };
}

// ============================================================ 2. ARENAS
// Six arenas, one per type, each applied to BOTH sides. The arena is chosen from
// the player's active card type exactly as it was before SC33 — seed-pure, so
// multiplayer stays deterministic. Effects are read by the engine at the point
// they matter; nothing here mutates.
export const ARENAS = {
  IRON: {
    key: 'IRON', label: 'THE FOUNDRY',
    banner: 'IRON cards hit 10% harder — both sides.',
    chip: 'IRON +10%', typeDmgPct: 10,
  },
  VOYAGE: {
    key: 'VOYAGE', label: 'THE CROSSING',
    banner: 'Swapping costs nothing — both sides.',
    chip: 'FREE SWAP', freeSwap: true,
  },
  HUSTLE: {
    key: 'HUSTLE', label: 'THE FLOOR',
    banner: '+1 energy a round — both sides.',
    chip: '+1 ENERGY', energyPerRound: 1,
  },
  MYSTIC: {
    key: 'MYSTIC', label: 'THE VEIL',
    banner: 'Both sides start with surge banked.',
    chip: 'FAST SURGE', chargeFaster: 1,
  },
  CHAOS: {
    key: 'CHAOS', label: 'THE SPILL',
    banner: '4 chip damage a round — both actives.',
    chip: 'CHIP 4', chipDmg: 4,
  },
  HEART: {
    key: 'HEART', label: 'THE HEARTH',
    banner: 'Both actives regen 4 a round.',
    chip: 'REGEN 4', regen: 4,
  },
};
export function arenaFor(type) { return ARENAS[type] || null; }

// ============================================================ 3. MOMENTUM
// The card that walks in over a fallen ally is ENRAGED for one round.
// Tuned: +2 energy on entry and +20% outgoing damage while it lasts. See the
// KO-margin distribution in the run log — this is the anti-blowout term.
export const ENRAGE = { energy: 2, dmgPct: 20, rounds: 1 };

// ============================================================ 4. PASSIVES
// Sixteen signature passives. Every LEGENDARY, MYTHIC, ALT and ETERNAL card
// carries exactly one. Assignment is: an explicit identity map first (where the
// card's own text or art demands a specific answer), then a deterministic hash
// of the card id into a type-appropriate pool. Same card, same passive, forever
// and on every client — there is no rng anywhere in this path.
//
// Each passive is expressed in the SAME effect-primitive vocabulary the support
// cards use, so the engine applies them at the points it already had hooks for.
export const PASSIVES = {
  secondWind: {
    key: 'secondWind', name: 'SECOND WIND',
    text: 'The first time this card would fall, it stays up at 1 HP — nothing charged, and slow, and swinging at half power.',
    hook: 'revive',
    // MEASURED +20.0pp bare. In a first-to-three-KOs game a denied knockout is a
    // third of the loss condition, so a free revive is the single strongest
    // thing a card can do. It keeps the promise (Jack asked for "revive once at
    // 1 HP") but pays for it: it comes back with no charge banked.
    // TUNING, in full, because this was the hardest number in SC33.
    //   revive, no cost ................. +18.6pp   (a denied KO is a third of
    //                                                the loss condition)
    //   revive + stunned a round ........  +0.3pp   (dies again immediately)
    //   revive + blinded a round ........  +9.4pp
    //   revive + blinded two rounds .....  +9.4pp   (identical — a 1 HP card
    //                                                never sees round two)
    //   revive + blinded + slowed .......  +9.9pp
    // Every "softer" cost is noise; only losing the action registers. So the
    // design is a FLOOR plus a DIAL: the card is stunned (the floor, +0.3pp) and
    // the bench heals in tribute (the dial). That is also the truer card — this
    // is Memento Ron, and a memorial should do something for the people left.
    // The dial reads ~0.09pp per point of bench healing: 20 gave +2.1pp, 40 the
    // +4.0pp below. A mythic signature should be worth chasing, so it is 40.
    // SC35 REFUTATION. Shipped as "stunned, uncharged, bench heals 40" and logged
    // at +2.8pp. Re-measured over FOUR disjoint seed blocks it is -0.9pp
    // (blocks +2.8/-3.5/-3.2/+0.4) — the +2.8 was block one, quoted alone.
    // The reason it is worth nothing is the same reason FIELD MEDIC was worth
    // nothing: BENCHED CARDS ARE NOT DAMAGED. They take no chip, no arena tick
    // and no splash, so they sit at full HP until they are sent in. Healing the
    // bench 40 heals nothing, so SC33's "dial" of ~0.09pp per point of bench
    // healing was a straight line fitted to noise, and what actually shipped was
    // the +0.3pp "revive at 1 HP, stunned" floor.
    // The real dial is the HP IT COMES BACK ON. 1 HP measured +0.3pp because a
    // 1 HP card dies again to the next thing that touches it.
    reviveFrac: 0.10, wipeCharge: true, stun: true, healBench: 40,
  },
  mirrorGuard: {
    key: 'mirrorGuard', name: 'MIRROR GUARD',
    text: 'The first HEAVY or SPECIAL that hits this card is reflected back at half power.',
    // Gated on SPECIAL alone it measured +0.0pp — specials are ~1.2 per battle
    // across both sides, so the passive simply never fired. Heavies are six
    // times as common; the trigger is the fix, not the number.
    // SC35: 50% measured +8.5pp over four seed blocks — OVER the +8pp budget.
    // SC33 read it at +5.9pp from a single block and called it the worst in
    // budget; it was the worst OUT of budget. 35% brings it back inside.
    hook: 'reflectSpecial', pct: 35, onHeavy: true,
  },
  benchMedic: {
    key: 'benchMedic', name: 'LAST ORDERS',
    text: 'When this card falls, whoever takes its place arrives fully charged.',
    // SC35. As FIELD MEDIC this fired 0.15 times a battle while its carrier was
    // knocked out in 80% of them — so it "fired" one time in five that it should
    // have. The missing four fifths are not a bug in the trigger, they are the
    // effect being void: the heal only emits when somebody on the bench is hurt,
    // and BENCHED CARDS ARE NEVER HURT. They take no chip damage, no arena tick
    // and no splash; they sit at full health until they are sent in.
    // So the payoff moves off the bench and onto the ONE card that is about to
    // matter: the ally walking in over the body. Full charge is real — a special
    // on the turn it lands — and it pays out every single time the carrier dies.
    // The bench heal is kept as flavour for the rare case there IS damage.
    hook: 'onKoHealBench', amount: 25, chargeHeir: true, healHeir: 34, heirEnergy: 3,
  },
  bulwark: {
    key: 'bulwark', name: 'BULWARK',
    text: 'Every incoming attack deals 5 less.',
    // SC35: gated on BASIC it fired 0.82 times a battle for +1.7pp, because
    // basics are about a third of the attacks thrown. All incoming attacks at 5
    // fires three to four times as often for a comparable per-hit bite.
    hook: 'basicResist', amount: 5, allAttacks: true,
  },
  siphon: {
    key: 'siphon', name: 'SIPHON',
    text: 'Heals for an eighth of the damage this card deals.',
    hook: 'siphon', pct: 12,      // 25% measured +9.9pp
  },
  overclock: {
    key: 'overclock', name: 'OVERCLOCK',
    text: 'Enters the arena with one charge pip already lit.',
    hook: 'startCharge', amount: 1,
  },
  warChest: {
    key: 'warChest', name: 'WAR CHEST',
    text: '+2 energy every round this card is active, and its attacks land 8% harder.',
    // SC35: as pure energy this fired 2.81 times a battle and measured -0.6pp,
    // with 0.26 payouts a battle landing on a FULL pool and evaporating. Energy
    // is simply not the binding resource once a card is established — income is
    // 2 a round against a heavy at 3, and the cap is 10. The energy is kept
    // (it is the card's identity) and the teeth are a damage rider, which is
    // binding and which fires on every swing.
    hook: 'energyPerRound', amount: 2, dmgPct: 13,
  },
  spite: {
    key: 'spite', name: 'SPITE',
    text: 'Deals 25% more damage while below 60% HP.',
    // SC35: at 40% it fired 0.47 times a battle for -0.2pp. A card under 40% is
    // usually one hit from gone, so the window opened just as it closed. 60%.
    hook: 'lowHpDmg', pct: 35, at: 0.6,
  },
  executioner: {
    key: 'executioner', name: 'EXECUTIONER',
    text: 'Deals 40% more damage to targets below 50% HP.',
    // SC35: at 30% it fired 0.39 times a battle for +0.4pp — a target under 30%
    // is usually killed by the ordinary attack before the bonus is ever needed.
    hook: 'finisher', pct: 30, at: 0.75,
  },
  stonewall: {
    key: 'stonewall', name: 'STONEWALL',
    text: 'The first hit this card takes each battle lands 30% softer.',
    hook: 'firstHitHalved', pct: 30,     // halving it measured +9.9pp
  },
  contagion: {
    key: 'contagion', name: 'CONTAGION',
    text: 'Anything this card hits deals 7 less damage on its next attack.',
    // SC35: this fired 2.80 times a battle and measured +0.9pp. It was not
    // failing to trigger — it was landing constantly and doing nothing, because
    // it borrowed the gastro "soiled" penalty of -2 against a median heavy of
    // 30. A signature passive on a legendary is worth more than 6% of one hit.
    hook: 'soilOnHit', soilAmount: 11,
  },
  unbowed: {
    key: 'unbowed', name: 'UNBOWED',
    text: 'Cannot be stunned, slowed or blinded — and nothing it swings is blocked.',
    // SC35: the deadest thing in the set. 0.03 triggers per battle — it fired
    // once every THIRTY-THREE battles, because stun, slow and blind between them
    // land almost never. There is no threshold to retune here; the condition
    // itself is the problem, so the card gains a second clause that cannot fail
    // to matter. UNBOWED now carries PIERCE: its attacks ignore GUARD, WARD,
    // BRACE and type resistance. That fires on every swing it takes, and it is
    // the same idea the name always claimed — nothing stops this card.
    hook: 'stunImmune', pierce: true, dmgPct: 12,
  },
  bloodMomentum: {
    key: 'bloodMomentum', name: 'MOMENTUM',
    text: 'Scoring a knockout heals this card 25 and charges it fully.',
    // SC35: fired 0.72 times a battle and measured -0.1pp. Both halves of the
    // old reward were paid in currencies the winner of an exchange already has
    // — after a knockout you are usually charging anyway and the energy pool is
    // near its cap. HP is the one thing a card that has just traded blows is
    // actually short of, so that is what the kill now pays.
    hook: 'onKoReward', charge: 9, energy: 3, heal: 12,
  },
  veilStep: {
    key: 'veilStep', name: 'VEIL STEP',
    text: 'After taking a HEAVY or SPECIAL, this card cannot be targeted next round.',
    hook: 'veilStep', onHeavy: true,     // SPECIAL-only measured +0.0pp
  },
  tithe: {
    key: 'tithe', name: 'TITHE',
    text: 'The enemy loses 1 energy at the end of every round this card is active.',
    hook: 'drainEnergy', amount: 1,
  },
  vanguard: {
    key: 'vanguard', name: 'VANGUARD',
    text: 'Always strikes first, and hits 8% harder for the tempo.',
    // SC35: initiative alone measured +1.7pp against a 1.75pp floor — real, but
    // inside touching distance of the noise. Striking first is worth something
    // and it is worth it on EVERY swing, so the rider is the same shape as the
    // effect rather than a bolted-on second idea.
    hook: 'initiative', until: 9999, amount: 500, dmgPct: 8,
  },
};
export const PASSIVE_KEYS = Object.keys(PASSIVES);

// Cards whose identity dictates the answer. These are the ones a player would
// be actively annoyed to see get a generic roll.
export const PASSIVE_BY_ID = {
  'memento-ron': 'secondWind',        // the card is literally a memorial
  'memento-ron-saint': 'secondWind',
  'damon-the-dome': 'mirrorGuard',    // the dome reflects
  'the-dome-eternal': 'mirrorGuard',
  'great-migration': 'benchMedic',    // it already revives; now it tends the bench
  'the-last-nomad': 'benchMedic',     // somebody has to watch the rest of it leave
  'incognegro': 'veilStep',           // STEALTH, made a passive
  'demon-gaze-sheren': 'executioner',
  'sherenza-throne': 'tithe',
  'labborn-james': 'overclock',
  'labborn-james-king': 'warChest',
  'deron-of-congo': 'vanguard',
  'henry-zach-viii': 'bulwark',
  'gastro-genesis': 'contagion',
  'the-first-dap': 'unbowed',
  'chalk-eternal': 'stonewall',
};

// Thematic pools per type, so an unmapped legendary still gets a passive that
// reads as belonging to it rather than a random draw from all sixteen.
const POOL_BY_TYPE = {
  IRON: ['bulwark', 'stonewall', 'unbowed', 'spite'],
  VOYAGE: ['vanguard', 'veilStep', 'overclock', 'warChest'],
  HUSTLE: ['bloodMomentum', 'warChest', 'executioner', 'overclock'],
  MYSTIC: ['mirrorGuard', 'veilStep', 'tithe', 'secondWind'],
  CHAOS: ['contagion', 'spite', 'executioner', 'bloodMomentum'],
  HEART: ['benchMedic', 'siphon', 'secondWind', 'stonewall'],
};

function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}

// Does this card carry a signature passive at all? LEGENDARY and up, plus any
// ALT printing (altOf) regardless of the rarity it was printed at.
export function hasPassive(c) {
  if (!c || c.role === 'support') return false;
  return rarityRank(c) >= RARITY_RANK.legendary || !!c.altOf;
}

export function passiveFor(c) {
  if (!hasPassive(c)) return null;
  const explicit = PASSIVE_BY_ID[c.id];
  if (explicit) return PASSIVES[explicit];
  const pool = POOL_BY_TYPE[c.type] || PASSIVE_KEYS;
  return PASSIVES[pool[hashStr(c.id) % pool.length]];
}
export function passiveKeyFor(c) { const p = passiveFor(c); return p ? p.key : null; }

// ============================================================ 5. THE GAUNTLET
// Three battles, no healing between them, ending at THE LAST NOMAD. Unlocked at
// 25 owned cards. The boss's ONE unique mechanic is LAST STAND, below.
export const GAUNTLET_UNLOCK_CARDS = 25;
export const GAUNTLET_LEGS = 3;

// The boss's five. THE LAST NOMAD leads; the rest are the caravan that followed
// it. The sim rewrote this entry twice, and both rewrites were measurements:
//
//   five ETERNALs (median 325) ............ player win  0.0% — a wall, not a boss
//   nomad + four LEGENDARIES (median 250) . player win  8.0% at FULL health
//   nomad + four EPICS (median 217) ....... player win 16.5%
//   nomad + four EPICS (median 170) ....... player win 48.0%   <- shipped
//
// The middle rows are the surprise. A x1.08 power ratio should sit near 45% by
// src/scaling.js's own calibration, and it measured 8%. The reason is that a
// boss deck built from LEGENDARIES gets everything SC33 added at once — five
// signature passives, a GRAND ARC synergy, and LAST STAND on top — and none of
// that is priced by cardPower. So the court is deliberately made of EPICS with
// no passives: the boss's edge is THE LAST NOMAD itself and its one mechanic,
// which is the fight the mode is supposed to be.
//
// SC35 — THE COURT IS NOW TWO, NOT FOUR.
//
// The gauntlet's pacing problem was that legs 1-2 eliminated 0.0% of the players
// who entered them, and the reason a toothy road could not be paid for anywhere
// else is squad size: you lose when THREE of yours are down, so arriving at a
// five-card boss with three cards means facing it with no spare bodies at all.
// Measured (bias [-0.30,-0.25], good referee, everything else held):
//   boss fields 5 -> leg 3 kills 81.1% of entrants, clear 16.7%
//   boss fields 4 -> leg 3 kills 76.4%,             clear 20.8%
//   boss fields 3 -> leg 3 kills 61.3%,             clear 34.2%
// Fielding three is also the truer card. It is THE LAST NOMAD; a horde was
// always the wrong picture.
//
// The two who remain are chosen so the boss KEEPS its synergy: IRON -> VOYAGE
// -> HUSTLE is an unbroken arc of three on the wheel, so the boss still runs a
// TYPE ARC and the game still uses the synergy system against you. Picking the
// first two of the old five instead would have left VOYAGE/MYSTIC/HEART — no
// arc at all — and quietly deleted that idea while looking like a truncation.
// The two are RARES, not the old epics, and that is a measured choice rather
// than a downgrade by accident. Holding everything else at the shipped SC35
// setting and moving only the court (good referee, N=300):
//   executive-pivot + pastel-pyramid (epic 170) -> leg 3 kills 77.6%, clear 17.7%
//   cubicle-wraith  + the-wire-room  (rare 138) -> leg 3 kills 65.8%, clear 27.0%
//   the-foam-oracle + unboxing-rajeev(rare 135) -> leg 3 kills 66.2%, clear 26.7%
// The last two are a coin-flip apart on the clear rate, and the tiebreak is that
// the shipped pair spreads the REFEREE LADDER widest — 24.3 / 26.7 / 32.7 across
// sloppy / good / strong, against 25.7 / 27.0 / 28.7 for the other. A boss that
// pays a strong player 8pp more than a sloppy one is a test of play; a boss that
// pays 3pp is a dice roll wearing a crown.
export const BOSS_DECK = {
  chars: ['the-last-nomad', 'the-foam-oracle', 'unboxing-rajeev'],
  supports: ['code-brown', 'the-913-word-hostage-memo'],
};
export const BOSS_ID = 'the-last-nomad';

// LAST STAND — the boss's single unique mechanic. The first of the boss's five
// to fall gets back up ONCE, at half HP, with a full charge. It is deliberately
// ONE mechanic and not two: an early version also started the boss on full
// charges across the board and the measured clear rate fell to 6%.
export const BOSS_MECHANIC = {
  key: 'lastStand', name: 'LAST STAND',
  text: 'The first of the boss’s five to fall gets back up once, at half HP, fully charged.',
};

// CARRY-OVER, shared by the app and the sim so the two cannot disagree.
//
// "No healing between battles" has two readings and the difference decides the
// whole mode. Reading one: the KO'd characters carry forward as KO'd, so a
// player who lost two on leg 1 walks into leg 2 needing a single further
// knockout to be eliminated. Measured: 68-76% of runs died on leg 2 and the
// clear rate sat at 0.3% no matter how weak the opponents were made. That is not
// a gauntlet, it is a hair trigger.
//
// Reading two, the one implemented: it is ATTRITION. A character knocked out is
// gone for the rest of the run — no revival, no healing, its HP is not restored
// — but the survivors carry their exact HP into a fresh first-to-three. Lose two
// on the road and you face the ASCENT with three cards and whatever health they
// have left. Nothing is healed; the squad simply gets smaller.
//
// SC35 — THE CAMP, and why the mode needed it.
//
// SC33 shipped "nothing is ever healed" and closed its log admitting legs 1-2
// eliminated essentially nobody, concluding that "the legs multiply, so a 12.8%
// clear rate is what you get the moment the road has teeth". The sweep it never
// ran shows the mechanism is worse than multiplication: the legs COUPLE.
//
// Measured (good referee, N=120, boss deck UNCHANGED in both rows):
//   bias [-0.46,-0.42] (shipped) -> leg 3 kills 67% of those who reach it
//   bias [-0.30,-0.25] (teeth)   -> leg 3 kills 85% of those who reach it
//
// The boss got 18pp deadlier without being touched, because a harder road
// delivers a smaller squad on less HP. So clear rate cannot be bought back at
// the boss — and it cannot be bought back by weakening the boss either: the
// court bottoms out at power 153 (the lowest four distinct-type epics in the
// set), and 110/130/150 all select the identical four cards. There is no
// weaker legal boss. The knob SC33 was turning had already hit its stop.
//
// THE CAMP breaks the coupling without giving up what the mode is about. A
// character knocked out is still gone for the whole run — that is the attrition
// and it is untouched. What changes is that the SURVIVORS bind their wounds
// between legs, recovering this fraction of their MISSING health. The road's
// price is therefore the CARDS it takes off you, not a HP death spiral that
// makes the last fight a formality decided two battles earlier.
export const GAUNTLET_REST = 1.0;

// Survivors of a leg, as the next leg's starting squad. Shared by the app and
// the sim so the two cannot disagree — and as of SC35 the app actually calls it
// (src/main.js used to inline its own copy of the filter/map, which is exactly
// the divergence this function was written to prevent).
export function gauntletCarry(sideChars, rest = GAUNTLET_REST) {
  return (sideChars || [])
    .filter(c => !c.koed)
    .map(c => {
      const max = c.maxHp || c.hp;
      const healed = Math.round(c.hp + (max - c.hp) * rest);
      return { id: c.id, hp: Math.max(1, Math.min(max, healed)) };
    });
}

// Difficulty ramp across the three legs, expressed as the opponent-power bias
// handed to draftOpponent. Leg 3 ignores it — the boss deck is curated.
// Legs 1 and 2 are deliberately EASIER than a standalone match. You fight three
// in a row with no healing, so a mirror-matched road is a lottery: at bias
// [0, +0.08] the sim measured 45% of runs dying on leg 1 and a 0.0% clear rate.
// Measured clear rate against the bias (good referee, 400 runs):
//   [-0.20,-0.15] ->  7.0%    [-0.35,-0.32] -> 19.8%
//   [-0.30,-0.25] -> 12.0%    [-0.42,-0.38] -> 26.8%
//   [-0.46,-0.42] -> 32.0%  <- shipped, mid-window
// At the shipped setting a GOOD player is not eliminated on legs 1-2; they are
// worn down there and tested at the boss. A weaker player still dies on the
// road — see the two-referee table in scripts/sc33-sim.js.
//
// SC35 — REBALANCED FOR PACING, and the biases are now ASYMMETRIC on purpose.
//
// At the SC33 setting the measured curve was leg 1 0.0% / leg 2 0.0% / leg 3
// 67.3% (conditional on reaching each leg, good referee). A player who was going
// to lose found out only in the last battle. That is the whole complaint.
//
// The non-obvious part is why leg 1's bias is HARDER than leg 2's, which reads
// backwards for a difficulty ramp. It is compensation, not a ramp: leg 2 inherits
// a squad that leg 1 has already thinned, so the same nominal bias is far deadlier
// there. Held at one shared bias, the split came out lopsided — at [-0.30,-0.25]
// leg 1 killed 2.5% and leg 2 killed 14.5%. Making leg 1 nominally harder and leg
// 2 nominally easier is what produces two legs that each kill about a tenth of the
// people who walk into them.
//
// TARGET CURVE (stated up front, then measured): each of legs 1 and 2 eliminates
// roughly 8-15% of those who enter it, leg 3 remains the real test at 60-75%, and
// a competent player clears the whole run about one run in four.
export const GAUNTLET_BIAS = [-0.16, -0.27, null];
export const GAUNTLET_LABELS = ['THE ROAD', 'THE ASCENT', 'THE LAST NOMAD'];

// Pack credits for clearing. Scaled to how far you got, so a run that dies on
// leg 3 is still worth having attempted.
export function gauntletReward(legsCleared) {
  const n = Math.max(0, Math.min(GAUNTLET_LEGS, legsCleared | 0));
  const parts = [];
  if (n >= 1) parts.push({ label: 'The Road', packs: 1 });
  if (n >= 2) parts.push({ label: 'The Ascent', packs: 2 });
  if (n >= 3) parts.push({ label: 'THE LAST NOMAD', packs: 5 });
  return { total: parts.reduce((a, p) => a + p.packs, 0), parts, cleared: n >= GAUNTLET_LEGS };
}
